import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChatCircleText,
  ClockCounterClockwise,
  Buildings,
  IdentificationCard,
  UserCircle,
  UsersThree,
  Bell,
  CreditCard,
  ShieldCheck,
  Warning,
  type Icon,
} from '@phosphor-icons/react';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet';

// Stripe-style settings panel: a top-anchored sheet that drops down across
// the full width of the viewport. Each tile navigates to a dedicated settings
// page inside DashboardShell — the panel itself is a launcher, not a settings
// container. Closes on tile click so the user lands on a regular page.

type GroupId = 'you' | 'organization' | 'activity' | 'help' | 'privacy';

interface Tile {
  id: string;
  to: string; // appended to /orgs/:slug
  i18nKey: string;
  descriptionKey: string;
  icon: Icon;
  group: GroupId;
  // disabled tiles still render so the surface looks complete; clicking them
  // is a no-op, and we mark them aria-disabled for accessibility.
  disabled?: boolean;
  // ownerOnly tiles are hidden entirely from non-owners (rather than just
  // disabled). The Danger Zone uses this — non-owners shouldn't even know it
  // exists, and the backend enforces the same rule (403 not_owner).
  ownerOnly?: boolean;
  // destructive tiles get the warning treatment: red icon, separated visually
  // at the bottom of the grid. Surface-level signal that the action is not
  // a routine settings change.
  destructive?: boolean;
}

// Render order of the section eyebrows. Personal first (Stripe/Linear pattern
// — users reach for settings thinking "I want to change MY..."), then the
// org-scoped bulk, then rarely-visited surfaces, with destructive last.
const GROUP_ORDER: readonly { id: GroupId; i18nKey: string }[] = [
  { id: 'you', i18nKey: 'settings.groups.you' },
  { id: 'organization', i18nKey: 'settings.groups.organization' },
  { id: 'activity', i18nKey: 'settings.groups.activity' },
  { id: 'help', i18nKey: 'settings.groups.help' },
  { id: 'privacy', i18nKey: 'settings.groups.privacy' },
];

const TILES: readonly Tile[] = [
  {
    id: 'profile',
    to: '/settings/profile',
    i18nKey: 'settings.profile.title',
    descriptionKey: 'settings.profile.description',
    icon: UserCircle,
    group: 'you',
  },
  {
    id: 'notifications',
    to: '/settings/notifications',
    i18nKey: 'settings.notifications.title',
    descriptionKey: 'settings.notifications.description',
    icon: Bell,
    group: 'you',
  },
  {
    id: 'organization',
    to: '/settings/organization',
    i18nKey: 'settings.organization.title',
    descriptionKey: 'settings.organization.description',
    icon: Buildings,
    group: 'organization',
  },
  {
    id: 'administrators',
    to: '/settings/administrators',
    i18nKey: 'settings.administrators.title',
    descriptionKey: 'settings.administrators.description',
    icon: UsersThree,
    group: 'organization',
  },
  {
    id: 'memberCardTemplates',
    to: '/settings/membercard-templates',
    i18nKey: 'settings.memberCardTemplates.title',
    descriptionKey: 'settings.memberCardTemplates.description',
    icon: IdentificationCard,
    group: 'organization',
  },
  {
    id: 'billing',
    to: '/settings/billing',
    i18nKey: 'settings.billing.title',
    descriptionKey: 'settings.billing.description',
    icon: CreditCard,
    group: 'organization',
  },
  {
    id: 'activity',
    to: '/settings/activity',
    i18nKey: 'settings.log.title',
    descriptionKey: 'settings.log.description',
    icon: ClockCounterClockwise,
    group: 'activity',
  },
  {
    id: 'feedback',
    to: '/settings/feedback',
    i18nKey: 'settings.feedback.title',
    descriptionKey: 'settings.feedback.description',
    icon: ChatCircleText,
    group: 'help',
  },
  {
    id: 'gdpr',
    to: '/settings/gdpr',
    i18nKey: 'settings.gdpr.title',
    descriptionKey: 'settings.gdpr.description',
    icon: ShieldCheck,
    group: 'privacy',
    disabled: true,
  },
  {
    id: 'dangerZone',
    to: '/settings/danger-zone',
    i18nKey: 'settings.dangerZone.title',
    descriptionKey: 'settings.dangerZone.description',
    icon: Warning,
    group: 'privacy',
    ownerOnly: true,
    destructive: true,
  },
];

interface Props {
  slug: string;
  // Caller's role in the org. Used to filter ownerOnly tiles. Empty string
  // when the org/role is still loading — treated as non-owner (safe default).
  role: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SettingsPanel({ slug, role, open, onOpenChange }: Props) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  // Close the panel automatically if the parent route changes — covers the
  // case where some other code (e.g. a redirect) navigates away while the
  // panel is open.
  useEffect(() => {
    if (!open) return;
    return () => {
      // intentional no-op cleanup; effect exists only to read `open`.
    };
  }, [open]);

  function pickTile(tile: Tile) {
    if (tile.disabled) return;
    onOpenChange(false);
    navigate(`/orgs/${slug}${tile.to}`);
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="top"
        className="h-[100dvh] max-h-[100dvh] overflow-y-auto border-b border-border bg-card pb-8 shadow-2xl sm:h-auto sm:max-h-[85dvh] sm:rounded-b-2xl"
      >
        <div className="mx-auto w-full max-w-[var(--content-max)] px-4 pt-6 sm:px-8 sm:pt-8">
          <div className="mb-6 flex flex-col gap-1">
            <SheetTitle className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">
              {t('settings.title')}
            </SheetTitle>
            <SheetDescription>{t('settings.subtitle')}</SheetDescription>
          </div>

          <div className="flex flex-col gap-8">
            {GROUP_ORDER.map((group) => {
              const tiles = TILES.filter(
                (tile) =>
                  tile.group === group.id && (!tile.ownerOnly || role === 'owner'),
              );
              if (tiles.length === 0) return null;
              return (
                <section key={group.id} className="flex flex-col gap-3">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t(group.i18nKey)}
                  </h3>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                    {tiles.map((tile) => {
                      const TileIcon = tile.icon;
                      const label = t(tile.i18nKey);
                      const description = t(tile.descriptionKey);
                      return (
                        <button
                          key={tile.id}
                          type="button"
                          onClick={() => pickTile(tile)}
                          disabled={tile.disabled}
                          aria-disabled={tile.disabled || undefined}
                          className={
                            'group flex min-h-[7.5rem] flex-col gap-3 rounded-xl border bg-card p-4 text-left transition-all duration-200 sm:p-5 ' +
                            (tile.destructive
                              ? 'border-destructive/30 hover:border-destructive '
                              : 'border-border hover:border-foreground/30 ') +
                            (tile.disabled
                              ? 'cursor-not-allowed opacity-50'
                              : 'hover:-translate-y-0.5 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring')
                          }
                        >
                          <span
                            className={
                              'flex h-10 w-10 items-center justify-center rounded-pill ' +
                              (tile.destructive
                                ? 'bg-destructive/10 text-destructive group-hover:bg-destructive/20'
                                : 'bg-muted text-foreground/80 group-hover:bg-accent-soft group-hover:text-foreground')
                            }
                          >
                            <TileIcon className="h-5 w-5" />
                          </span>
                          <div className="flex min-w-0 flex-col gap-0.5">
                            <span
                              className={
                                'truncate font-heading text-sm font-semibold ' +
                                (tile.destructive ? 'text-destructive' : 'text-foreground')
                              }
                            >
                              {label}
                              {tile.disabled && (
                                <span className="ml-2 text-xs font-medium text-muted-foreground">
                                  {t('settings.comingSoon')}
                                </span>
                              )}
                            </span>
                            <span className="line-clamp-2 text-xs text-muted-foreground">
                              {description}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
